import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import React from 'react';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // You can also log the error to an error reporting service here
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        globalThis.location.reload();
    };

    handleGoHome = () => {
        globalThis.location.href = '/admin';
    };

    override render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center bg-white rounded-xl border border-slate-200 shadow-sm mt-8 mx-auto max-w-2xl">
                    <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6">
                        <AlertTriangle size={32} />
                    </div>

                    <h2 className="text-2xl font-bold text-slate-900 mb-2">
                        Something went wrong
                    </h2>

                    <p className="text-slate-500 mb-8 max-w-md">
                        We encountered an unexpected error while trying to render this page.
                        Please try reloading or return to the dashboard.
                    </p>

                    <div className="flex gap-4">
                        <button
                            onClick={this.handleReset}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-slate-900 transition-colors font-medium text-sm"
                        >
                            <RefreshCcw size={16} />
                            Reload Page
                        </button>

                        <button
                            onClick={this.handleGoHome}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium text-sm"
                        >
                            <Home size={16} />
                            Back to Dashboard
                        </button>
                    </div>

                    {this.state.error && (
                        <details className="mt-8 w-full max-w-full text-left">
                            <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
                                Show technical details
                            </summary>
                            <div className="mt-2 p-4 bg-slate-50 rounded-lg overflow-auto border border-slate-200">
                                <p className="font-mono text-xs text-red-600 break-words">
                                    {this.state.error.toString()}
                                </p>
                            </div>
                        </details>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
